import React, { useState, useMemo } from 'react';
import T from 'prop-types';
import { get, mapValues, max, isEmpty, min, values } from 'lodash-es';

import { AppImpactDropdown, appImpactMetrics } from '../../MetricLists';
import { SmartChart } from '../../../../../components/Chart';
import Skeleton from '../../../../../components/Skeleton';
import { appImpactChartConfig } from './config';
import { aiCohortsColorsMap } from '../../../configs/constants';
import { advancedPartialFilters } from '../../../configs/chart-config';

const orderedCohorts = ['high_frequency_ai_users', 'never_ai_users'];

const cohortsNameOverrides = {
  high_frequency_ai_users: 'High-Frequency AI Users',
  never_ai_users: 'Never AI Users',
};

const emptyObject = {};

const AppImpactChart = ({ settings, filters, data }) => {
  const [metric, setMetric] = useState(Object.keys(appImpactMetrics)[0]);

  const series = get(data, ['time_series'], {});

  const seriesByMetric = useMemo(
    () =>
      orderedCohorts.reduce(
        (acc, cohort) => ({
          ...acc,
          [cohort]: get(series, [cohort, metric], []),
        }),
        {}
      ),
    [series, metric]
  );

  const chartConfig = useMemo(() => {
    const minPoints = mapValues(seriesByMetric, (d) =>
      isEmpty(d) ? 0 : min(d.map((item) => item.value))
    );
    const maxPoints = mapValues(seriesByMetric, (d) =>
      isEmpty(d) ? 0 : max(d.map((item) => item.value))
    );
    const minPoint = min(values(minPoints));
    const maxPoint = max(values(maxPoints));
    const deviation = (maxPoint - minPoint) * 0.1;
    const floorPoint = Math.floor(minPoint - deviation);
    const ceilPoint = Math.ceil(maxPoint + deviation);

    return appImpactChartConfig({
      metric,
      floor: floorPoint < 0 && minPoint >= 0 ? 0 : floorPoint,
      ceiling: ceilPoint,
      granularity: filters.granularity,
    });
  }, [metric, seriesByMetric, filters.granularity]);

  const seriesData = useMemo(
    () => (isEmpty(seriesByMetric) ? emptyObject : seriesByMetric),
    [seriesByMetric]
  );

  const metaSeriesData = useMemo(
    () =>
      mapValues(seriesData, (_, key) => ({
        color: aiCohortsColorsMap[key],
        dataLabels: {
          crop: false,
          enabled: false,
          overflow: 'allow',
          padding: 3,
          useHTML: true,
        },
        name: cohortsNameOverrides[key],
        threshold: 0,
        type: 'spline',
        visible: true,
      })),
    [seriesData]
  );

  const partialFilters = useMemo(
    () => ({
      ...advancedPartialFilters,
      granularity: filters.granularity,
    }),
    [filters.granularity]
  );

  return (
    <div className="app-impact-chart">
      <header className="app-impact-chart__header">
        <AppImpactDropdown selected={metric} onChange={setMetric} />
        <div className="app-impact-chart__header__subtitle">
          <b>{cohortsNameOverrides.high_frequency_ai_users}</b> vs{' '}
          <b>{cohortsNameOverrides.never_ai_users}</b>
        </div>
      </header>
      {settings.pending ? (
        <Skeleton height={328} className="d-flex" />
      ) : (
        <div className="app-impact-chart__body">
          <SmartChart
            chartId="app-impact-chart"
            series={seriesData}
            metaSeriesData={metaSeriesData}
            config={chartConfig}
            allowPartialPeriods
            filters={partialFilters}
          />
          <div className="ai-dashboard-legend">
            {orderedCohorts.map((cohort) => (
              <div key={cohort} className="ai-dashboard-legend__item">
                <span
                  className="ai-dashboard-legend__marker"
                  style={{ backgroundColor: aiCohortsColorsMap[cohort] }}
                />
                <span>{cohortsNameOverrides[cohort]}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

AppImpactChart.propTypes = {
  settings: T.shape({
    pending: T.bool,
  }).isRequired,
  data: T.shape({
    time_series: T.object,
  }).isRequired,
  filters: T.shape({
    granularity: T.string,
  }).isRequired,
};

export default AppImpactChart;
